import React, { useState, useEffect } from 'react';
import { transactionAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { InsuranceReimbursement } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  category_id: number | null;
  subcategory_id: number | null;
  is_transfer: boolean;
  confidence_score: number | null;
  linked_account_id: number;
  manual_account_id: number | null;
  account_name: string;
  category_name?: string;
  subcategory_name?: string;
  category_color?: string;
  // Enhanced fields
  is_verified?: boolean;
  verification_date?: string;
  categorization_source?: 'manual' | 'rule' | 'system';
  categorized_by_rule_id?: number;
  original_amount?: number;
  reimbursed_amount?: number;
  reimbursement_date?: string;
  insurance_provider?: string;
  is_reimbursable?: boolean;
  is_business_expense?: boolean;
  business_category?: string;
  notes?: string;
  total_reimbursed_amount?: number;
}

interface TransferCounterpart {
  id: number;
  date: string;
  amount: number;
  description: string | null;
  merchant_name: string | null;
  account_name: string;
}

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onUpdate: (updatedTransaction: Transaction) => void;
  initialTab?: 'details' | 'notes';
}

export const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onUpdate,
  initialTab = 'details',
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'notes'>(initialTab);
  const [reimbursements, setReimbursements] = useState<InsuranceReimbursement[]>([]);
  const [loadingReimbursements, setLoadingReimbursements] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedAmount, setEditedAmount] = useState<string>('');
  const [editedDescription, setEditedDescription] = useState<string>('');
  const [editedMerchantName, setEditedMerchantName] = useState<string>('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [transferCounterpart, setTransferCounterpart] = useState<TransferCounterpart | null | undefined>(undefined);
  const [loadingCounterpart, setLoadingCounterpart] = useState(false);

  useEffect(() => {
    if (transaction) {
      setNotes(transaction.notes || '');
      setEditedNotes(transaction.notes || '');
      setEditedAmount(transaction.amount.toString());
      setEditedDescription(transaction.description || '');
      setEditedMerchantName(transaction.merchant_name || '');
      setError(null);
      setIsEditing(false);
      setIsEditingNotes(false);
      setActiveTab(initialTab);
      setTransferCounterpart(undefined);
    }

    if (transaction && transaction.amount < 0 && isOpen) {
      loadReimbursements();
    } else {
      setReimbursements([]);
    }

    if (transaction && transaction.is_transfer && isOpen) {
      loadTransferCounterpart();
    }
  }, [transaction, isOpen, initialTab]);

  const loadReimbursements = async () => {
    if (!transaction) return;
    try {
      setLoadingReimbursements(true);
      const data = await transactionAPI.getInsuranceReimbursements(transaction.id);
      const expenseReimbursements = data.filter((r: InsuranceReimbursement) => r.expense_transaction_id === transaction.id);
      setReimbursements(expenseReimbursements);
    } catch (err) {
      console.error('Error loading reimbursements:', err);
    } finally {
      setLoadingReimbursements(false);
    }
  };

  const loadTransferCounterpart = async () => {
    if (!transaction) return;
    try {
      setLoadingCounterpart(true);
      const data = await transactionAPI.getTransferCounterpart(transaction.id);
      setTransferCounterpart(data); // null if not found, object if found
    } catch (err) {
      console.error('Error loading transfer counterpart:', err);
      setTransferCounterpart(null);
    } finally {
      setLoadingCounterpart(false);
    }
  };

  const handleNotesUpdate = async () => {
    if (!transaction) return;
    try {
      setLoading(true);
      await transactionAPI.updateTransactionNotes(transaction.id, { notes: editedNotes });
      setNotes(editedNotes);
      onUpdate({ ...transaction, notes: editedNotes });
      setIsEditingNotes(false);
      setError(null);
    } catch (err) {
      console.error('Error updating notes:', err);
      setError('Failed to update notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelNotesEdit = () => {
    if (transaction) {
      setEditedNotes(transaction.notes || '');
    }
    setIsEditingNotes(false);
    setError(null);
  };

  const handleSaveDetails = async () => {
    if (!transaction) return;
    try {
      setLoading(true);
      setError(null);

      const updateData: { amount?: number; description?: string; merchant_name?: string | null } = {};

      if (parseFloat(editedAmount) !== transaction.amount) {
        updateData.amount = parseFloat(editedAmount);
      }
      if (editedDescription !== transaction.description) {
        updateData.description = editedDescription;
      }
      if (editedMerchantName !== (transaction.merchant_name || '')) {
        updateData.merchant_name = editedMerchantName || null;
      }

      if (Object.keys(updateData).length === 0) {
        setIsEditing(false);
        return;
      }

      const updatedTransaction = await transactionAPI.updateTransaction(transaction.id, updateData);
      onUpdate(updatedTransaction);
      setIsEditing(false);
      setError(null);
      setEditedAmount(updatedTransaction.amount.toString());
      setEditedDescription(updatedTransaction.description || '');
      setEditedMerchantName(updatedTransaction.merchant_name || '');
    } catch (err: any) {
      console.error('Error updating transaction:', err);
      setError(err.message || 'Failed to update transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (transaction) {
      setEditedAmount(transaction.amount.toString());
      setEditedDescription(transaction.description || '');
      setEditedMerchantName(transaction.merchant_name || '');
    }
    setIsEditing(false);
    setError(null);
  };

  return (
    <Dialog open={isOpen && !!transaction} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transaction Details</DialogTitle>
        </DialogHeader>

        {transaction && (
          <div className="modal-body">
            {/* Transaction Summary */}
            <div className="transaction-summary">
              <div className="transaction-main-info">
                <h3>{transaction.merchant_name || transaction.description}</h3>
                <div className="transaction-meta">
                  <span className="date">{new Date(transaction.date).toLocaleDateString()}</span>
                  <span className="account">{transaction.account_name}</span>
                  <span className={`amount ${transaction.amount > 0 ? 'income' : 'expense'}`}>
                    {formatCurrency(transaction.amount)}
                  </span>
                </div>
              </div>

              {transaction.category_name && (
                <div className="transaction-category">
                  <span
                    className="category-badge"
                    style={{ backgroundColor: transaction.category_color }}
                  >
                    {transaction.category_name}
                  </span>
                  {transaction.subcategory_name && (
                    <span className="subcategory-badge">
                      {transaction.subcategory_name}
                    </span>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="error-message">
                <p>{error}</p>
              </div>
            )}

            {/* Tabs */}
            <div className="details-tabs">
              <button
                className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
              <button
                className={`tab-btn ${activeTab === 'notes' ? 'active' : ''}`}
                onClick={() => setActiveTab('notes')}
              >
                Notes
              </button>
            </div>

            {/* Tab Content */}
            <div className="tab-content">
              {activeTab === 'details' && (
                <div className="details-panel">
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <div className="detail-value">
                      <span className={`status-badge ${transaction.is_verified ? 'verified' : 'unverified'}`}>
                        {transaction.is_verified ? '✓ Verified' : '○ Unverified'}
                      </span>
                      {transaction.is_business_expense && (
                        <span className="status-badge business">💼 Business</span>
                      )}
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Categorization:</span>
                    <span className="detail-value">
                      {transaction.categorization_source === 'manual' ? 'Manual' :
                       transaction.categorization_source === 'rule' ? '⚙️ Rule-based' :
                       transaction.categorization_source === 'system' ? '🤖 System' : 'Manual'}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Amount:</span>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={editedAmount}
                        onChange={(e) => setEditedAmount(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid var(--border-color, #333)',
                          borderRadius: '4px',
                          background: 'var(--background-dark)',
                          color: 'var(--text-primary, #fff)',
                          fontSize: '0.875rem',
                          width: '200px'
                        }}
                      />
                    ) : (
                      <span className="detail-value">{formatCurrency(transaction.amount)}</span>
                    )}
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Description:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid var(--border-color, #333)',
                          borderRadius: '4px',
                          background: 'var(--background-dark)',
                          color: 'var(--text-primary, #fff)',
                          fontSize: '0.875rem',
                          width: '100%',
                          maxWidth: '400px'
                        }}
                      />
                    ) : (
                      <span className="detail-value">{transaction.description}</span>
                    )}
                  </div>

                  {(isEditing || transaction.merchant_name) && (
                    <div className="detail-row">
                      <span className="detail-label">Merchant:</span>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editedMerchantName}
                          onChange={(e) => setEditedMerchantName(e.target.value)}
                          placeholder="Optional merchant name"
                          style={{
                            padding: '0.5rem',
                            border: '1px solid var(--border-color, #333)',
                            borderRadius: '4px',
                            background: 'var(--background-dark)',
                            color: 'var(--text-primary, #fff)',
                            fontSize: '0.875rem',
                            width: '100%',
                            maxWidth: '400px'
                          }}
                        />
                      ) : (
                        <span className="detail-value">{transaction.merchant_name || '—'}</span>
                      )}
                    </div>
                  )}

                  {isEditing && (
                    <div className="detail-row" style={{ marginTop: '1rem', gap: '0.5rem', display: 'flex' }}>
                      <button
                        onClick={handleSaveDetails}
                        disabled={loading}
                        className="btn btn-primary"
                        style={{ padding: '0.5rem 1rem' }}
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={loading}
                        className="btn btn-secondary"
                        style={{ padding: '0.5rem 1rem' }}
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  {!isEditing && (
                    <div className="detail-row" style={{ marginTop: '1rem' }}>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="btn btn-secondary"
                        style={{ padding: '0.5rem 1rem' }}
                      >
                        Edit Details
                      </button>
                    </div>
                  )}

                  {/* Transfer Section */}
                  {transaction.is_transfer && (
                    <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px', marginTop: '1rem' }}>
                      <span className="detail-label" style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Transfer</span>
                      {loadingCounterpart ? (
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Loading counterpart...</span>
                      ) : transferCounterpart ? (
                        <div style={{
                          width: '100%',
                          background: 'var(--background-card)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '0.75rem 1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}>
                          <div className="detail-row" style={{ margin: 0 }}>
                            <span className="detail-label">Account:</span>
                            <span className="detail-value">{transferCounterpart.account_name}</span>
                          </div>
                          <div className="detail-row" style={{ margin: 0 }}>
                            <span className="detail-label">Date:</span>
                            <span className="detail-value">{new Date(transferCounterpart.date).toLocaleDateString()}</span>
                          </div>
                          <div className="detail-row" style={{ margin: 0 }}>
                            <span className="detail-label">Amount:</span>
                            <span className={`detail-value ${transferCounterpart.amount > 0 ? 'income' : 'expense'}`}>
                              {formatCurrency(transferCounterpart.amount)}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                          Counterpart transaction not found
                        </span>
                      )}
                    </div>
                  )}

                  {/* Reimbursement Information - Only for expense transactions */}
                  {transaction.amount < 0 && transaction.total_reimbursed_amount !== undefined && transaction.total_reimbursed_amount > 0 && (
                    <div className="detail-row">
                      <span className="detail-label">Insurance Reimbursement:</span>
                      <span className="detail-value" style={{
                        color: transaction.total_reimbursed_amount >= Math.abs(transaction.amount) ? '#10b981' : '#f59e0b',
                        fontWeight: '500'
                      }}>
                        {formatCurrency(transaction.total_reimbursed_amount)} of {formatCurrency(Math.abs(transaction.amount))} reimbursed
                        {transaction.total_reimbursed_amount >= Math.abs(transaction.amount) ? ' (Fully)' : ' (Partially)'}
                      </span>
                    </div>
                  )}

                  {/* Reimbursement Details Table */}
                  {transaction.amount < 0 && reimbursements.length > 0 && (
                    <div className="detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                      <span className="detail-label">Reimbursement Details:</span>
                      <div style={{
                        width: '100%',
                        background: 'var(--background-card, #1a1a1a)',
                        border: '1px solid var(--border-color, #333)',
                        borderRadius: '8px',
                        padding: '1rem',
                        overflowX: 'auto'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border-color, #333)', textAlign: 'left' }}>
                              <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)' }}>Date</th>
                              <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)' }}>Income Transaction</th>
                              <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>Amount Covered</th>
                              <th style={{ padding: '0.75rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-secondary, #888)', textAlign: 'right' }}>% Covered</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reimbursements.map((reimbursement) => {
                              const expenseTotal = Math.abs(transaction.amount);
                              const percentCovered = expenseTotal > 0 ? ((reimbursement.covered_amount / expenseTotal) * 100).toFixed(1) : '0.0';

                              return (
                                <tr key={reimbursement.id} style={{ borderBottom: '1px solid var(--border-color, #333)' }}>
                                  <td style={{ padding: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                                    {reimbursement.income_date ? formatDate(reimbursement.income_date) : '-'}
                                  </td>
                                  <td style={{ padding: '0.75rem', fontWeight: '500', color: 'var(--text-primary, #fff)' }}>
                                    {reimbursement.income_description || 'Unknown income'}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '500', color: 'var(--success-color, #10B981)' }}>
                                    {formatCurrency(reimbursement.covered_amount)}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.875rem', color: 'var(--text-secondary, #888)' }}>
                                    {percentCovered}%
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'notes' && (
                <div className="notes-panel">
                  <div className="form-group">
                    <label>Transaction Notes</label>
                    {isEditingNotes ? (
                      <textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Add any additional notes about this transaction..."
                        rows={6}
                        className="notes-textarea"
                        style={{
                          padding: '0.75rem',
                          border: '1px solid var(--border-color, #333)',
                          borderRadius: '4px',
                          background: 'var(--background-dark)',
                          color: 'var(--text-primary, #fff)',
                          fontSize: '0.875rem',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '0.75rem',
                          border: '1px solid var(--border-color, #333)',
                          borderRadius: '4px',
                          background: 'var(--background-dark)',
                          color: notes ? 'var(--text-primary, #fff)' : 'var(--text-secondary, #888)',
                          fontSize: '0.875rem',
                          minHeight: '150px',
                          whiteSpace: 'pre-wrap',
                          wordWrap: 'break-word'
                        }}
                      >
                        {notes || 'No notes added'}
                      </div>
                    )}
                  </div>

                  <div className="form-actions" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                    {isEditingNotes ? (
                      <>
                        <button
                          className="btn btn-primary"
                          onClick={handleNotesUpdate}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save Notes'}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handleCancelNotesEdit}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-secondary"
                        onClick={() => setIsEditingNotes(true)}
                      >
                        Edit Notes
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer" style={{ marginTop: '1.5rem' }}>
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
